import InfoBlock from 'components/UI/Blocks/InfoBlock'
import Button from 'components/UI/Buttons/Button'
import SliderInput from 'components/UI/Inputs/SliderInput'
import Typography from 'components/UI/Typography'
import Heading from 'components/UI/Typography/Heading'
import { useAppDispatch, useAppSelector } from 'hooks'
import { useCreditDecision } from 'hooks/useCreditDecision'
import { DecisionViews } from 'pages/Decision/constants'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import { useGetInsuranceSumMutation } from 'redux/api/pledgeApi'
import { type LoanParams, loanParamsSelector, propertySelector, setLoanParams } from 'redux/store/slices/decisionSlice'
import { getLoanInformation } from 'redux/store/slices/loanSlice'
import tw from 'twin.macro'
import { numberWithSpaces } from 'utils'
import LoanCalculator from 'utils/loan-calculator'
import { isScoringOptionsArray } from 'utils/type-guards'

import BankMonitoringConditions from './BankMonitoringConditions'
import LoanTermsAgreement from './LoanTermsAgreement'

const LoanParamsChoosing = () => {
  //Tools
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  // State
  const [openConditions, setOpenConditions] = useState(false)
  const [isTermsAgreed, setIsTermsAgreed] = useState(true)

  // Selectors
  const { amount, period } = useAppSelector(loanParamsSelector)
  const { loanRepaymentMethod } = useAppSelector(getLoanInformation)
  const property = useAppSelector(propertySelector)

  // Mutations
  const [calculateInsurance, { isLoading }] = useGetInsuranceSumMutation()

  // Custom hooks
  const { selectedSubProgram, onMainViewChange } = useCreditDecision()

  // Computed values
  const isSelectedProgramOwnWithoutPledge = useMemo(
    () => isScoringOptionsArray(selectedSubProgram),
    [selectedSubProgram]
  )

  const matchedOption = useMemo(() => {
    if (!selectedSubProgram || !period) return
    if (!isScoringOptionsArray(selectedSubProgram)) return
    return selectedSubProgram.options.find(option => option.credit_params.period === +period)
  }, [period, selectedSubProgram])

  const interestRate = useMemo(() => {
    if (!selectedSubProgram) return
    if (isScoringOptionsArray(selectedSubProgram)) {
      if (!matchedOption) return
      return matchedOption.credit_params.interest_rate
    }
    return selectedSubProgram.options.credit_params.interest_rate
  }, [matchedOption, selectedSubProgram])

  const minPeriod = useMemo(() => {
    if (!selectedSubProgram) return 0
    if (isScoringOptionsArray(selectedSubProgram)) {
      return Math.min(...selectedSubProgram.options.map(option => option.credit_params.period))
    }
    return selectedSubProgram.min_period
  }, [selectedSubProgram])

  const maxPeriod = useMemo(() => {
    if (!selectedSubProgram) return 0
    if (isScoringOptionsArray(selectedSubProgram)) {
      return Math.max(...selectedSubProgram.options.map(option => option.credit_params.period))
    }
    return selectedSubProgram.max_period
  }, [selectedSubProgram])

  const maxPrincipal = useMemo(() => {
    if (!selectedSubProgram) return 0
    if (isSelectedProgramOwnWithoutPledge) {
      if (!matchedOption) return 0
      return matchedOption.credit_params.credit_sum
    }
    if (property) {
      return +property.programs.credit_params.loan_amount
    }
    return selectedSubProgram.max_loan_amount ?? 0
  }, [isSelectedProgramOwnWithoutPledge, matchedOption, property, selectedSubProgram])

  const monthlyPayment = useMemo(() => {
    if (!amount || !period || !interestRate || !selectedSubProgram || !loanRepaymentMethod) return
    const isDummyProgram = Object.keys(selectedSubProgram.options).length === 0
    if (isDummyProgram) return

    return new LoanCalculator(+amount, +period, +interestRate, loanRepaymentMethod).getCalcResults().monthlyPayment
  }, [amount, interestRate, loanRepaymentMethod, period, selectedSubProgram])

  // Handlers
  const handleCalculateLoanInsurance = async () => {
    try {
      if (!selectedSubProgram || !selectedSubProgram.with_pledges || !amount || !period) return
      await calculateInsurance({
        amount: +amount,
        period: +period
      }).unwrap()
    } catch (error) {
      console.log(error)
    }
  }

  const handleLoanParamsChange = (key: keyof LoanParams, val: string) => {
    if (key === 'period' && isSelectedProgramOwnWithoutPledge && isScoringOptionsArray(selectedSubProgram)) {
      const matchedOption = selectedSubProgram.options.find(option => option.credit_params.period === +val)
      if (matchedOption) {
        dispatch(setLoanParams({ amount: Number(matchedOption.credit_params.loan_amount).toString() }))
      }
    }
    dispatch(setLoanParams({ [key]: val }))
  }

  return (
    <div tw='flex flex-col'>
      <div tw='space-y-3'>
        <InfoBlock twStyle={tw`space-y-4`}>
          <Typography variant='body' weight='bold'>
            {t('loan.amount', { ns: 'common' })}
          </Typography>
          {selectedSubProgram ? (
            <SliderInput
              key={selectedSubProgram.id}
              value={amount}
              step={100_000}
              min={+selectedSubProgram.min_loan_amount}
              max={maxPrincipal}
              unit='₸'
              thousandsSeparator=' '
              onChange={val => handleLoanParamsChange('amount', val)}
              renderMinText={min => `${numberWithSpaces(min)} ₸`}
              renderMaxText={max => `${numberWithSpaces(max)} ₸`}
            />
          ) : (
            <div>
              <Skeleton tw='h-[52px] rounded-2xl' />
              <div tw='flex justify-between'>
                <Skeleton tw='h-3 w-24 rounded-2xl' />
                <Skeleton tw='h-3 w-24 rounded-2xl' />
              </div>
            </div>
          )}
        </InfoBlock>
        <InfoBlock twStyle={tw`space-y-4`}>
          <Typography variant='body' weight='bold'>
            {t('loan.period', { ns: 'common' })}
          </Typography>
          {selectedSubProgram ? (
            <SliderInput
              key={selectedSubProgram.id}
              value={period}
              min={minPeriod}
              max={maxPeriod}
              unit='мес.'
              step={isSelectedProgramOwnWithoutPledge ? 12 : 1}
              isInputDisabled={isSelectedProgramOwnWithoutPledge}
              onChange={val => handleLoanParamsChange('period', val)}
              renderMinText={min => t('calculator.period-limit', { ns: 'common', period: min })}
              renderMaxText={max => t('calculator.period-limit', { ns: 'common', period: max })}
            />
          ) : (
            <div>
              <Skeleton tw='h-[52px] rounded-2xl' />
              <div tw='flex justify-between'>
                <Skeleton tw='h-3 w-24 rounded-2xl' />
                <Skeleton tw='h-3 w-24 rounded-2xl' />
              </div>
            </div>
          )}
        </InfoBlock>
        <div tw='grid grid-cols-2 gap-x-1 md:gap-x-3'>
          <InfoBlock twStyle={tw`space-y-1`}>
            <Typography variant='caption' twStyle={tw`text-grey-dark`}>
              {t('loan.interest-rate', { ns: 'common' })}
            </Typography>
            {selectedSubProgram ? (
              <Heading as='h3' variant='subtitle'>
                {interestRate}%
              </Heading>
            ) : (
              <Skeleton tw='h-5 w-12 rounded-lg' />
            )}
          </InfoBlock>
          <InfoBlock twStyle={tw`space-y-1`}>
            <Typography variant='caption' twStyle={tw`text-grey-dark`}>
              {t('loan.monthly-payment', { ns: 'common' })}
            </Typography>
            <Heading as='h3' variant='subtitle'>
              {monthlyPayment && !isNaN(monthlyPayment) ? `${numberWithSpaces(Math.round(monthlyPayment))} ₸` : '-'}
            </Heading>
          </InfoBlock>
        </div>
        <LoanTermsAgreement
          isTermsAgreed={isTermsAgreed}
          onShowAgreement={() => setOpenConditions(true)}
          onAgree={() => setIsTermsAgreed(true)}
        />
      </div>
      <div tw='px-4 md:px-0 mt-8 md:mt-6 flex items-center flex-col lg:flex-row justify-between'>
        <Typography
          as='span'
          variant='subbody'
          weight='bold'
          twStyle={tw` p-4 text-primary-middle cursor-pointer  text-base font-medium`}
          onClick={() => onMainViewChange(DecisionViews.PROGRAMS)}
        >
          {t('change-program')}
        </Typography>
        <Button
          variant='primary'
          loading={isLoading}
          disabled={!isTermsAgreed || !amount || !period || !monthlyPayment}
          onClick={() => {
            onMainViewChange(DecisionViews['LOAN-CONFIRMATION'])
            if (selectedSubProgram?.with_pledges) {
              handleCalculateLoanInsurance()
            }
          }}
        >
          <Typography variant='body' weight='bold' twStyle={tw`text-white`}>
            {t('flow-continue')}
          </Typography>
          <Typography variant='caption' twStyle={tw`text-white`}>
            {t('flow-go-to-goal')}
          </Typography>
        </Button>
      </div>
      <BankMonitoringConditions
        open={openConditions}
        isTermAgreed={isTermsAgreed}
        onClose={() => setOpenConditions(false)}
        onConfirm={() => {
          setIsTermsAgreed(true)
          setOpenConditions(false)
          handleCalculateLoanInsurance()
        }}
      />
    </div>
  )
}

export default LoanParamsChoosing
